import {
	BLOCKER_SIZE,
	DASH_TRACK,
	distancesHome,
	edgeKey,
	GRID_PEOPLE,
	GRID_PETS,
	type LogicGridState,
	laddersFrom,
	type PlayerIndex,
	type RouteBlockerState,
	type SnailDashState,
	type WordLadderState,
} from "@playpal/core";
import { useEffect, useState } from "react";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Word Ladder Race -----------------------------------------------------

export function WordLadderBoard({ state, seat, onMove }: BoardProps<WordLadderState>) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	const choices = laddersFrom(state.at[seat]);

	return (
		<div className="board-frame board-lg">
			<div className="mb-4 flex items-center justify-center gap-3 text-2xl font-black">
				<span className="rounded-2xl bg-white/70 px-4 py-2 dark:bg-white/10">{state.start}</span>
				<span aria-hidden="true">→</span>
				<span className="rounded-2xl bg-emerald-200 px-4 py-2 dark:bg-emerald-900/60">
					{state.target}
				</span>
			</div>

			<div className="mb-4 grid grid-cols-2 gap-3 text-center">
				{([seat, rival] as PlayerIndex[]).map((who, index) => (
					<div
						key={who}
						className={`rounded-2xl px-3 py-2 ${
							index === 0 ? "bg-rose-100 dark:bg-rose-950/50" : "bg-sky-100 dark:bg-sky-950/50"
						}`}
					>
						<p className="text-sm font-semibold opacity-80">{index === 0 ? "You" : "Them"}</p>
						<p className="text-2xl font-black">{state.at[who]}</p>
						<p className="text-sm opacity-70">{state.paths[who].length - 1} rungs</p>
					</div>
				))}
			</div>

			<p className="mb-2 text-center text-lg font-semibold">
				{myTurn ? "Pick a word one letter away" : "Waiting for your friend…"}
			</p>

			<div className="flex flex-wrap justify-center gap-2">
				{choices.map((word) => (
					<button
						key={word}
						type="button"
						disabled={!myTurn}
						onClick={() => onMove({ word })}
						className={`rounded-2xl px-4 py-3 text-xl font-bold shadow transition ${
							word === state.target
								? "bg-emerald-400 text-emerald-950"
								: "bg-white text-indigo-950 dark:bg-white/15 dark:text-white"
						} disabled:opacity-40`}
					>
						{word}
					</button>
				))}
			</div>
		</div>
	);
}

// --- Snail Dash -----------------------------------------------------------

export function SnailDashBoard({
	state,
	seat,
	onMove,
	clockOffset,
}: BoardProps<SnailDashState> & { clockOffset: number }) {
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	// Redrawn often so the beat ring keeps time with the music in your head.
	// Corrected for clock skew, like every other timed board: `beatAt` is the
	// server's clock, and in a game that is entirely about tapping on the beat,
	// drawing the ring against an uncorrected local clock would put the visible
	// beat a whole skew away from the one being scored.
	const [now, setNow] = useState(() => Date.now() + clockOffset);

	useEffect(() => {
		if (state.result) return;
		const timer = setInterval(() => setNow(Date.now() + clockOffset), 60);
		return () => clearInterval(timer);
	}, [state.result, clockOffset]);

	const untilBeat = state.beatAt - now;
	// Swells from nothing up to the beat, so there is something to tap *to*.
	const closeness = Math.max(0, Math.min(1, 1 - Math.abs(untilBeat) / 700));

	return (
		<div className="board-frame board-lg">
			{([seat, rival] as PlayerIndex[]).map((who, index) => (
				<div key={who} className="mb-3">
					<div className="mb-1 flex justify-between text-sm font-semibold opacity-80">
						<span>{index === 0 ? "You" : "Them"}</span>
						<span>{state.lastBeat[who] === "hit" ? `🔥 ${state.streak[who]}` : ""}</span>
					</div>
					<div className="relative h-10 rounded-full bg-white/60 dark:bg-white/10">
						<div
							className="absolute top-1 text-2xl transition-[left] duration-200"
							style={{ left: `${(state.position[who] / DASH_TRACK) * 88}%` }}
						>
							🐌
						</div>
						<span className="absolute top-2 right-2 text-xl" aria-hidden="true">
							🏁
						</span>
					</div>
				</div>
			))}

			<div className="mt-6 flex justify-center">
				<button
					type="button"
					disabled={state.tapped[seat] || state.result !== null}
					onClick={() => onMove({ tap: true })}
					aria-label="Tap on the beat"
					className="relative flex h-40 w-40 items-center justify-center rounded-full bg-indigo-600 text-4xl font-black text-white shadow-lg transition active:scale-95 disabled:opacity-40"
				>
					<span
						className="absolute inset-0 rounded-full border-4 border-white"
						style={{ opacity: closeness, transform: `scale(${1.4 - closeness * 0.4})` }}
						aria-hidden="true"
					/>
					{state.tapped[seat] ? (state.lastBeat[seat] === "hit" ? "✅" : "✖️") : "TAP"}
				</button>
			</div>
		</div>
	);
}

// --- Route Blocker --------------------------------------------------------

export function RouteBlockerBoard({ state, seat, onMove }: BoardProps<RouteBlockerState>) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	const [fencing, setFencing] = useState(false);
	const fences = new Set(state.fences);
	const [mine, theirs] = distancesHome(state);

	const rowOf = (square: number) => Math.floor(square / BLOCKER_SIZE);
	const canStepTo = (square: number) => {
		if (!myTurn || fencing) return false;
		const from = state.tokens[seat];
		const rowGap = Math.abs(rowOf(square) - rowOf(from));
		const columnGap = Math.abs((square % BLOCKER_SIZE) - (from % BLOCKER_SIZE));
		if (rowGap + columnGap !== 1) return false;
		if (fences.has(edgeKey(from, square))) return false;
		return state.tokens[rival] !== square;
	};

	/**
	 * A fence sits on the line between two squares, so that is what you tap. The
	 * bars only appear while fencing is switched on, which keeps them out of the
	 * way of ordinary moves — the squares are already small enough on a phone.
	 */
	const fenceBar = (from: number, to: number, vertical: boolean) => {
		if (!fencing || fences.has(edgeKey(from, to))) return null;
		return (
			<button
				type="button"
				aria-label={vertical ? `Fence right of square ${from}` : `Fence below square ${from}`}
				onClick={() => {
					onMove({ fence: [from, to] });
					setFencing(false);
				}}
				className={`absolute z-10 rounded-full bg-amber-500/70 hover:bg-amber-500 ${
					vertical
						? "top-1 -right-1 h-[calc(100%-0.5rem)] w-2"
						: "-bottom-1 left-1 h-2 w-[calc(100%-0.5rem)]"
				}`}
			/>
		);
	};

	return (
		<div className="board-frame board-md">
			<div className="mb-3 flex items-center justify-between text-sm font-semibold">
				<span>You: {mine ?? "—"} squares to go</span>
				<span>Them: {theirs ?? "—"}</span>
			</div>

			<button
				type="button"
				disabled={!myTurn || state.fencesLeft[seat] === 0}
				onClick={() => setFencing((on) => !on)}
				className={`mb-3 w-full rounded-2xl px-4 py-3 font-bold shadow transition disabled:opacity-40 ${
					fencing
						? "bg-amber-400 text-amber-950"
						: "bg-white text-indigo-950 dark:bg-white/15 dark:text-white"
				}`}
			>
				🚧{" "}
				{fencing
					? "Tap a line between two squares"
					: `Drop a fence (${state.fencesLeft[seat]} left)`}
			</button>

			<div
				className="grid gap-2"
				style={{ gridTemplateColumns: `repeat(${BLOCKER_SIZE}, minmax(0, 1fr))` }}
			>
				{Array.from({ length: BLOCKER_SIZE * BLOCKER_SIZE }, (_, square) => {
					const isMe = state.tokens[seat] === square;
					const isThem = state.tokens[rival] === square;
					const steppable = canStepTo(square);
					const hasRight = square % BLOCKER_SIZE < BLOCKER_SIZE - 1;
					const hasBelow = rowOf(square) < BLOCKER_SIZE - 1;

					return (
						<div key={square} className="relative">
							<button
								type="button"
								disabled={!steppable}
								aria-label={`Square ${square}`}
								onClick={() => onMove({ step: square })}
								className={`aspect-square w-full rounded-lg text-xl transition ${
									isMe
										? "bg-rose-400"
										: isThem
											? "bg-sky-400"
											: steppable
												? "bg-emerald-200 dark:bg-emerald-800"
												: "bg-white/70 dark:bg-white/10"
								} disabled:cursor-default`}
							>
								{isMe ? "🔴" : isThem ? "🔵" : ""}
							</button>
							{hasRight && fenceBar(square, square + 1, true)}
							{hasBelow && fenceBar(square, square + BLOCKER_SIZE, false)}
							{/* Fences already dropped, drawn whether or not you are placing one. */}
							{hasRight && fences.has(edgeKey(square, square + 1)) && (
								<span className="absolute top-1 -right-1 h-[calc(100%-0.5rem)] w-2 rounded-full bg-amber-600" />
							)}
							{hasBelow && fences.has(edgeKey(square, square + BLOCKER_SIZE)) && (
								<span className="absolute -bottom-1 left-1 h-2 w-[calc(100%-0.5rem)] rounded-full bg-amber-600" />
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

// --- Logic Grid Duel ------------------------------------------------------

export function LogicGridBoard({ state, seat, onMove }: BoardProps<LogicGridState>) {
	const myTurn = state.turn === seat && !state.result;
	// Your working grid, which nobody else sees until you submit it.
	const [grid, setGrid] = useState<(number | null)[]>(() => GRID_PEOPLE.map(() => null));

	const assign = (person: number, pet: number) => {
		setGrid((current) => {
			const next = [...current];
			// A pet belongs to one person, so giving it away takes it back first.
			for (let i = 0; i < next.length; i++) if (next[i] === pet) next[i] = null;
			next[person] = next[person] === pet ? null : pet;
			return next;
		});
	};

	const complete = grid.every((pet) => pet !== null);
	const mine = state.guesses.filter((guess) => guess.player === seat);

	return (
		<div className="board-frame board-lg">
			<ul className="mb-4 space-y-1 rounded-2xl bg-white/70 p-4 text-lg dark:bg-white/10">
				{state.clues.map((clue) => (
					<li key={`${clue.person}-${clue.pet}-${clue.has}`}>
						{GRID_PEOPLE[clue.person]} {clue.has ? "has" : "does not have"} the{" "}
						<span className="text-2xl">{GRID_PETS[clue.pet]}</span>
					</li>
				))}
			</ul>

			<div className="mb-4 space-y-2">
				{GRID_PEOPLE.map((person, personIndex) => (
					<div key={person} className="flex items-center gap-2">
						<span className="w-14 font-bold">{person}</span>
						{GRID_PETS.map((pet, petIndex) => (
							<button
								key={pet}
								type="button"
								disabled={!myTurn}
								aria-label={`${person} has the pet ${petIndex + 1}`}
								aria-pressed={grid[personIndex] === petIndex}
								onClick={() => assign(personIndex, petIndex)}
								className={`flex-1 rounded-xl py-2 text-2xl transition ${
									grid[personIndex] === petIndex ? "bg-emerald-400" : "bg-white/70 dark:bg-white/10"
								} disabled:opacity-40`}
							>
								{pet}
							</button>
						))}
					</div>
				))}
			</div>

			<button
				type="button"
				disabled={!myTurn || !complete}
				onClick={() => onMove({ grid })}
				className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-lg font-bold text-white shadow disabled:opacity-40"
			>
				{complete ? "Check my answer" : "Give everyone a pet"}
			</button>

			{mine.length > 0 && (
				<ul className="mt-4 space-y-1 text-center text-sm font-semibold">
					{mine.slice(-3).map((guess, index) => (
						<li key={`${guess.correct}-${index}`}>
							{guess.grid.map((pet) => GRID_PETS[pet]).join(" ")} — {guess.correct} right
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
